// SPDX-License-Identifier: BUSL-1.1

/**
 * React Query bundle for the agent-detail page — fetches agent/anthropic
 * fallback, sessions, jobs, HB-03 events, and exposes the terminate-all
 * mutation.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 * Pure data-wiring move; same query keys, same enabled gates as before.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { showApiError, showSuccess } from '@/lib/toast';
import type { Session } from '@/types/api';
import type { ActivityEvent, Job } from './types';

export function useAgentDetailQueries(id: string | undefined) {
  const qc = useQueryClient();

  // Try unified agents API first, fallback to Anthropic agents API
  const agent = useQuery({
    queryKey: ['agents', 'unified', id],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: () => apiFetch<any>(`/agents/${id}`),
    enabled: !!id,
  });

  // Fallback to Anthropic agents API if unified returns error
  const anthropicAgent = useQuery({
    queryKey: ['agents', 'anthropic', id],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: () => apiFetch<any>(`/pi-agents/${id}`),
    enabled: !!id && !agent.data,
  });

  const sessions = useQuery({
    queryKey: ['sessions', { agentId: id }],
    // Fetch up to 100 (the API max) so the DataTable's client-side pagination
    // dropdown (10/25/50/100) has enough rows to slice. Going past 100 would
    // need cursor-based pagination — tracked as a follow-up.
    queryFn: () => apiFetch<{ data: Session[] }>(`/agents/${id}/sessions?limit=100`),
    enabled: !!id,
  });

  // Jobs query (for unified agents)
  const jobs = useQuery({
    queryKey: ['jobs', { agentId: id }],
    queryFn: () => apiFetch<{ data: Job[] }>(`/agents/${id}/jobs?limit=100`),
    enabled: !!id && !!agent.data?.data,
  });

  // HB-03 server-side activity events (tool_call / artifact / decision /
  // context_compressed / blocker / task_started / task_completed). Distinct
  // from the in-memory "Activity" tab below, which just merges sessions+jobs.
  // Events live in ops_agent_activity; the endpoint supports cursor-based
  // pagination via `?before=ISO`. We fetch the first page of 100 here;
  // a follow-up can wire the cursor through for infinite scroll.
  const events = useQuery({
    queryKey: ['events', { agentId: id }],
    queryFn: () =>
      apiFetch<{ events: ActivityEvent[]; nextCursor: string | null }>(
        `/agents/${id}/activity?limit=100`,
      ),
    enabled: !!id && !!agent.data?.data,
  });

  const terminate = useMutation({
    mutationFn: async (sessionIds: string[]) =>
      Promise.all(
        sessionIds.map((sid) =>
          apiFetch(`/sessions/${sid}`, { method: 'DELETE' }),
        ),
      ),
    onSuccess: () => {
      showSuccess('All sessions terminated');
      qc.invalidateQueries({ queryKey: ['sessions', { agentId: id }] });
      qc.invalidateQueries({ queryKey: ['agents', id] });
    },
    onError: (err) => showApiError(err),
  });

  return { agent, anthropicAgent, sessions, jobs, events, terminate };
}
